/**
 * The single mapping from every app status to a badge tone and label. Pages never pick a tone for a status
 * themselves: they call `statusBadge(domain, value)` (or render <StatusBadge>), so "in transit" is the same blue
 * and "exception" the same red on every screen of both apps.
 *
 * Tone meaning (one language everywhere):
 *  - success: done / healthy / live      - info: in progress, active, informational
 *  - warning: needs attention soon        - danger: failed, overdue, exception, blocking
 *  - neutral: inactive, closed, mock, not started
 *  - accent: brand highlight, never a status (not used here)
 */
import type { BadgeTone } from "./components.js";

export type StatusSpec = { label: string; tone: BadgeTone; live?: boolean };

/** Shipment lifecycle (@flexfreight/core SHIPMENT_STATUSES). */
export const SHIPMENT_STATUS = {
  draft: { label: "Draft", tone: "neutral" },
  booked: { label: "Booked", tone: "info" },
  dispatched: { label: "Dispatched", tone: "info" },
  picked_up: { label: "Picked up", tone: "info" },
  in_transit: { label: "In transit", tone: "info", live: true },
  out_for_delivery: { label: "Out for delivery", tone: "info", live: true },
  delivered: { label: "Delivered", tone: "success" },
  exception: { label: "Exception", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Support tickets (console Tickets). */
export const TICKET_STATUS = {
  open: { label: "Open", tone: "info" },
  pending: { label: "Waiting on customer", tone: "neutral" },
  on_hold: { label: "On hold", tone: "warning" },
  solved: { label: "Solved", tone: "success" },
  closed: { label: "Closed", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

export const TICKET_PRIORITY = {
  urgent: { label: "Urgent", tone: "danger" },
  high: { label: "High", tone: "warning" },
  normal: { label: "Normal", tone: "neutral" },
  low: { label: "Low", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

export const TICKET_SLA = {
  breached: { label: "SLA breached", tone: "danger" },
  due_soon: { label: "Due within the hour", tone: "warning" },
  on_track: { label: "On track", tone: "neutral" },
  met: { label: "Target met", tone: "success" },
  paused: { label: "Paused", tone: "neutral" },
  none: { label: "No target", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Quote rating status (db quote_status). */
export const QUOTE_STATUS = {
  pending: { label: "Rating", tone: "info", live: true },
  rated: { label: "Rated", tone: "info" },
  partial: { label: "Partial", tone: "warning" },
  failed: { label: "No rates", tone: "danger" },
  booked: { label: "Booked", tone: "success" },
  expired: { label: "Expired", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Quote sales outcome (console Quotes). */
export const QUOTE_OUTCOME = {
  open: { label: "Open", tone: "info" },
  won: { label: "Won", tone: "success" },
  lost: { label: "Lost", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** House carrier connection mode (db house_carrier_mode / carrier_mode). */
export const CARRIER_MODE = {
  mock: { label: "Mock", tone: "neutral" },
  sandbox: { label: "Sandbox", tone: "info" },
  live: { label: "Live", tone: "success" },
} as const satisfies Record<string, StatusSpec>;

/** A carrier connection as carrier health shows it: its mode, or Off when the carrier is turned off. */
export const CARRIER_CONNECTION = {
  ...CARRIER_MODE,
  off: { label: "Off", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Integration lifecycle (Zoho, Microsoft 365, Flex AI, carrier credentials). */
export const INTEGRATION_STATE = {
  "Awaiting credentials": { label: "Awaiting credentials", tone: "neutral" },
  "Needs a passing test": { label: "Needs a passing test", tone: "warning" },
  "Ready to turn on": { label: "Ready to turn on", tone: "info" },
  Syncing: { label: "Syncing", tone: "success" },
  Connected: { label: "Connected", tone: "success" },
  not_set: { label: "Not set", tone: "neutral" },
  incomplete: { label: "Incomplete", tone: "warning" },
  connected: { label: "Connected", tone: "success" },
  test_failed: { label: "Test failed", tone: "danger" },
  unverified: { label: "Saved, not verified", tone: "info" },
  untested: { label: "Saved, not tested", tone: "info" },
  /** Configured but switched off (sign-in codes, Google Maps). */
  Off: { label: "Off", tone: "warning" },
  /** EagleLoad: a key is saved and quoting runs through it / a key is saved but quoting is off. */
  On: { label: "On", tone: "success" },
  "Key saved": { label: "Key saved", tone: "info" },
  /** Marketing: nothing can be sent until outbound marketing is approved. */
  "Sending off": { label: "Sending off", tone: "neutral" },
  /** Flex AI without an Anthropic key. */
  "Demonstration mode": { label: "Demonstration mode", tone: "neutral" },
  /** Zoho: the shared connection, Desk two-way, Books auto-invoice. */
  "Awaiting client keys": { label: "Awaiting client keys", tone: "neutral" },
  "Awaiting authorization": { label: "Awaiting authorization", tone: "warning" },
  "Awaiting Zoho connection": { label: "Awaiting Zoho connection", tone: "neutral" },
  "Awaiting organization": { label: "Awaiting organization", tone: "neutral" },
  "Two-way": { label: "Two-way", tone: "success" },
  "Auto-invoicing": { label: "Auto-invoicing", tone: "success" },
  "Using server keys": { label: "Using server keys", tone: "info" },
  Mock: { label: "Mock", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Customer invoices as the console shows them. */
export const INVOICE_STATUS = {
  open: { label: "Open", tone: "info" },
  issued: { label: "Issued", tone: "info" },
  overdue: { label: "Overdue", tone: "danger" },
  paid: { label: "Paid", tone: "success" },
  void: { label: "Void", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Customer account kind and sign-in state (customers list and users table). */
export const CUSTOMER_KIND = {
  customer: { label: "Customer", tone: "neutral" },
  prospect: { label: "Prospect", tone: "warning" },
} as const satisfies Record<string, StatusSpec>;

export const USER_SIGNIN = {
  disabled: { label: "Disabled", tone: "neutral" },
  invite_pending: { label: "Invite pending", tone: "warning" },
  mfa: { label: "2-step on", tone: "success" },
  password: { label: "Password", tone: "info" },
} as const satisfies Record<string, StatusSpec>;

/** Knowledge base articles. */
export const ARTICLE_STATUS = {
  draft: { label: "Draft", tone: "neutral" },
  published: { label: "Published", tone: "success" },
} as const satisfies Record<string, StatusSpec>;

/** Marketing campaigns (two-person approval). */
export const CAMPAIGN_STATUS = {
  draft: { label: "Draft", tone: "neutral" },
  pending_approval: { label: "Waiting for approval", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  scheduled: { label: "Scheduled", tone: "info" },
} as const satisfies Record<string, StatusSpec>;

/** Marketing contact reachability by email. */
export const CONTACT_REACH = {
  reachable: { label: "Reachable", tone: "success" },
  unreachable: { label: "Not reachable", tone: "neutral" },
  suppressed: { label: "Suppressed", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** "Report a problem" reports (Admin Center > Problem reports). */
export const PROBLEM_STATUS = {
  new: { label: "New", tone: "warning" },
  triaged: { label: "Triaged", tone: "info" },
  fixed: { label: "Fixed", tone: "success" },
  wont_fix: { label: "Won't fix", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Scheduled report emails (labels match the API's SCHEDULE_STATE_LABEL). */
export const REPORT_SCHEDULE = {
  active: { label: "Active", tone: "success" },
  paused: { label: "Paused", tone: "neutral" },
  waiting_for_email: { label: "Waiting for email to be connected", tone: "warning" },
  failing: { label: "Last send failed", tone: "danger" },
} as const satisfies Record<string, StatusSpec>;

/** Report catalog availability and measured-or-not metrics (Reports landing). */
export const REPORT_AVAILABILITY = {
  ready: { label: "Ready", tone: "success" },
  integration_required: { label: "Integration required", tone: "neutral" },
  no_source: { label: "No data source", tone: "neutral" },
  not_measured: { label: "Not measured", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Console staff accounts (Team & permissions). */
export const STAFF_ACCOUNT = {
  active: { label: "Active", tone: "success" },
  deactivated: { label: "Deactivated", tone: "neutral" },
  invite_pending: { label: "Invite pending", tone: "warning" },
} as const satisfies Record<string, StatusSpec>;

/** Two-step sign-in for a staff member. */
export const TWO_STEP = {
  on: { label: "On", tone: "success" },
  required_off: { label: "Required, off", tone: "danger" },
  off: { label: "Off", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Staff "view as customer" sessions. */
export const VIEW_AS_MODE = {
  read: { label: "View only", tone: "info" },
  write: { label: "Can make changes", tone: "warning" },
} as const satisfies Record<string, StatusSpec>;

export const MARKUP_RULE = {
  active: { label: "Active", tone: "success" },
  off: { label: "Off", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Freight claims (core CLAIM_STATUS_TEXT labels). Filed waits on us: warning. */
export const CLAIM_STATUS = {
  draft: { label: "Draft", tone: "neutral" },
  filed: { label: "Filed", tone: "warning" },
  under_review: { label: "Under review", tone: "info" },
  approved: { label: "Approved", tone: "info" },
  denied: { label: "Denied", tone: "danger" },
  paid: { label: "Paid", tone: "success" },
  withdrawn: { label: "Withdrawn", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Net-terms credit applications (console Credit). */
export const CREDIT_APPLICATION = {
  pending: { label: "Waiting for review", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  withdrawn: { label: "Withdrawn", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Self-serve signup review (console Signups & approvals). */
export const SIGNUP_STATUS = {
  pending: { label: "Needs review", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  hold: { label: "On hold", tone: "info" },
  rejected: { label: "Rejected", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Sales leads (console Leads). */
export const LEAD_STATUS = {
  new: { label: "New", tone: "info" },
  contacted: { label: "Contacted", tone: "neutral" },
  qualified: { label: "Qualified", tone: "success" },
  converted: { label: "Converted", tone: "success" },
  lost: { label: "Lost", tone: "neutral" },
} as const satisfies Record<string, StatusSpec>;

/** Carrier bill against the quoted cost (core flagText labels). */
export const BILL_FLAG = {
  unbilled: { label: "Unbilled", tone: "neutral" },
  match: { label: "Match", tone: "success" },
  over: { label: "Over", tone: "danger" },
  under: { label: "Under", tone: "info" },
} as const satisfies Record<string, StatusSpec>;

/** Dispute with the carrier over a bill (core disputeText labels). */
export const BILL_DISPUTE = {
  none: { label: "No dispute", tone: "neutral" },
  open: { label: "Disputed", tone: "warning" },
  won: { label: "Dispute won", tone: "success" },
  lost: { label: "Dispute lost", tone: "danger" },
} as const satisfies Record<string, StatusSpec>;

/** Where a figure or contact comes from: sample (mock) data is never shown as real. */
export const DATA_SOURCE = {
  mock: { label: "Sample", tone: "warning" },
  sandbox: { label: "Sandbox", tone: "info" },
  live: { label: "Live", tone: "success" },
} as const satisfies Record<string, StatusSpec>;

export const STATUS_MAPS = {
  shipment: SHIPMENT_STATUS,
  ticket: TICKET_STATUS,
  ticketPriority: TICKET_PRIORITY,
  ticketSla: TICKET_SLA,
  quote: QUOTE_STATUS,
  quoteOutcome: QUOTE_OUTCOME,
  carrierMode: CARRIER_MODE,
  carrierConnection: CARRIER_CONNECTION,
  integration: INTEGRATION_STATE,
  invoice: INVOICE_STATUS,
  customerKind: CUSTOMER_KIND,
  userSignin: USER_SIGNIN,
  markupRule: MARKUP_RULE,
  claim: CLAIM_STATUS,
  creditApplication: CREDIT_APPLICATION,
  signup: SIGNUP_STATUS,
  lead: LEAD_STATUS,
  billFlag: BILL_FLAG,
  billDispute: BILL_DISPUTE,
  dataSource: DATA_SOURCE,
  article: ARTICLE_STATUS,
  campaign: CAMPAIGN_STATUS,
  contactReach: CONTACT_REACH,
  problem: PROBLEM_STATUS,
  reportSchedule: REPORT_SCHEDULE,
  reportAvailability: REPORT_AVAILABILITY,
  staffAccount: STAFF_ACCOUNT,
  twoStep: TWO_STEP,
  viewAs: VIEW_AS_MODE,
} as const;
export type StatusDomain = keyof typeof STATUS_MAPS;

/** "picked_up" -> "Picked up": the label for a value no map knows yet. */
export function humanizeStatus(value: string): string {
  const t = value.replace(/[_-]+/g, " ").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Unknown";
}

/** Tone + label for any app status. Unknown values read neutral with a humanized label (never a crash or blank). */
export function statusBadge(domain: StatusDomain, value: string | null | undefined): StatusSpec {
  const map = STATUS_MAPS[domain] as Record<string, StatusSpec>;
  if (value && Object.prototype.hasOwnProperty.call(map, value)) return map[value]!;
  return { label: value ? humanizeStatus(value) : "Unknown", tone: "neutral" };
}

/** Options for a status filter (Select / MultiSelect / StatusFilter) straight from a domain map. */
export function statusOptions(domain: StatusDomain): { value: string; label: string; tone: BadgeTone }[] {
  return Object.entries(STATUS_MAPS[domain] as Record<string, StatusSpec>).map(([value, s]) => ({ value, label: s.label, tone: s.tone }));
}
